import { CRED_STORAGE_FILE, CRED_STORAGE_KEYRING } from "./cli_config.js";
import { credentialsPath, defaultDir, resolveConfigFile } from "./paths.js";
import { writeSecretFile } from "./secure_file.js";
import { existsSync, readFileSync } from "node:fs";
import { isAbsolute, relative, resolve } from "node:path";
//#region src/profiles.ts
/**
* Pointer-only profiles avoid mirrored credentials and persistent active-profile
* state while preserving existing single-account and legacy-directory installs.
*/
const PROFILES_FILE = "profiles.json";
const KEYRING_CREDENTIALS = "keyring";
const isKeyringPointer = (credentials) => credentials === KEYRING_CREDENTIALS;
/** The implicit profile. Backed by plain `credentials.json`, with or without a profiles file. */
const DEFAULT_PROFILE = "DEFAULT";
/** Profile names become part of a filename, so keep them boring. */
const NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;
const locationOf = (profile) => profile.storage === "keyring" ? {
	profile: profile.name,
	storage: CRED_STORAGE_KEYRING
} : {
	profile: profile.name,
	storage: CRED_STORAGE_FILE,
	path: profile.credentialsPath
};
const credentialsDisplay = (profile) => profile.storage === "keyring" ? KEYRING_CREDENTIALS : profile.credentialsPath;
/** Which profile this invocation should use: `--profile` → `NEON_PROFILE` → `DEFAULT`. */
const selectProfileName = (flag, env = process.env) => nonEmpty(flag) ?? nonEmpty(env.NEON_PROFILE) ?? "DEFAULT";
const assertValidProfileName = (name) => {
	if (!NAME_PATTERN.test(name)) throw new Error(`Invalid profile name "${name}". Use letters, digits, dot, dash or underscore, starting with a letter or digit.`);
};
/** Where `profiles.json` lives for this config directory (whether or not it exists yet). */
const profilesFilePath = (dir) => resolveConfigFile(PROFILES_FILE, dir === defaultDir ? {} : { dir }).path;
/**
* Read and classify `profiles.json` without deciding what to do about it.
*
* Entry keys and shapes are validated here rather than at each use. A key is a profile name,
* and a name that `assertValidProfileName` would reject cannot have been written by this CLI —
* it would travel into error messages as a recovery command nobody can run, and into a
* `credentials.<name>.json` filename.
*/
const inspectProfiles = (dir) => {
	const path = profilesFilePath(dir);
	if (!existsSync(path)) return { kind: "absent" };
	const broken = (why) => ({
		kind: "unusable",
		reason: `${path} could not be read as a profiles file: ${why}`
	});
	let contents;
	try {
		contents = readFileSync(path, "utf8");
	} catch (err) {
		const code = err.code;
		return broken(code ? `reading it failed with ${code}` : "reading it failed");
	}
	let parsed;
	try {
		parsed = JSON.parse(contents);
	} catch {
		return broken("it is not valid JSON");
	}
	if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return broken("it does not contain an object");
	const profiles = parsed.profiles;
	if (profiles === null || typeof profiles !== "object" || Array.isArray(profiles)) return broken("it has no `profiles` object");
	for (const [name, entry] of Object.entries(profiles)) {
		if (!NAME_PATTERN.test(name)) return broken(`"${name}" is not a valid profile name`);
		if (entry === null || typeof entry !== "object" || typeof entry.credentials !== "string" || entry.credentials.trim() === "") return broken(`profile "${name}" has no \`credentials\` pointer`);
	}
	return {
		kind: "ok",
		file: {
			version: 1,
			profiles
		}
	};
};
/**
* Read `profiles.json`, or `null` when there is nothing usable there.
*
* A malformed file is reported through `onWarn` and treated as absent, because for a *read* the
* worst case is a named profile turning up missing, which is recoverable — whereas throwing
* would lock the user out of `neon auth` itself. Writing is the opposite: see
* {@link upsertProfile}, which refuses rather than rebuilding a file it cannot read.
*/
const readProfiles = (dir, onWarn = () => {}) => {
	const read = inspectProfiles(dir);
	if (read.kind === "ok") return read.file;
	if (read.kind === "unusable") onWarn(read.reason);
	return null;
};
/**
* Storage cannot be trusted when the profiles file cannot be read.
*
* Call this **before** anything that writes a credential, opens a browser, or spends an API
* call. {@link upsertProfile} refuses too, but it runs last: by then `create` has already
* overwritten `credentials.<name>.json` and revoked the key it replaced, and `neon auth
* --profile` has already signed in over it — a refusal that arrives after the destruction it
* exists to prevent. The path resolution itself is the unsound part, since with the metadata
* unreadable the conventional filename is a guess about which account that file belongs to.
*
* DEFAULT follows the same rule because the broken file may be its only
* keyring pointer.
*/
const assertProfilesUsable = (dir, name) => {
	const read = inspectProfiles(dir);
	if (read.kind === "unusable") throw new Error(`${read.reason}. Fix or delete the file before working with profile "${name}" — it is the only record of where each account's credentials live.`);
};
/** Resolve a profile to an absolute credentials path. Throws when a named profile is unknown. */
const resolveProfile = (dir, name) => {
	const read = inspectProfiles(dir);
	if (read.kind === "unusable") throw new Error(`${read.reason}. Fix or delete the file — every profile is defined in it.`);
	const file = read.kind === "ok" ? read.file : null;
	const entry = file?.profiles[name];
	if (entry) {
		if (isKeyringPointer(entry.credentials)) return {
			name,
			storage: CRED_STORAGE_KEYRING,
			...entry.label ? { label: entry.label } : {},
			...entry.userId ? { userId: entry.userId } : {},
			declared: true
		};
		return {
			name,
			storage: CRED_STORAGE_FILE,
			credentialsPath: resolveEntryPath(dir, entry.credentials),
			...entry.label ? { label: entry.label } : {},
			...entry.userId ? { userId: entry.userId } : {},
			declared: true
		};
	}
	if (name === "DEFAULT") return {
		name,
		storage: CRED_STORAGE_FILE,
		credentialsPath: credentialsPath(dir),
		declared: false
	};
	const known = file ? Object.keys(file.profiles).join(", ") : DEFAULT_PROFILE;
	throw new Error(`Unknown profile "${name}". Known profiles: ${known}. Create it with \`neon profile create ${name}\`.`);
};
/** Default location for a new named profile's credentials file. */
const newProfileCredentialsPath = (dir, name) => resolve(dir, `credentials.${name}.json`);
/**
* Record a profile, creating `profiles.json` if this is the first named one.
*
* When the file is created, `DEFAULT` is written explicitly and pointed at wherever
* `credentials.json` actually is. That matters for an install predating the directory
* rename: `profiles.json` is created in `neon/` while the credentials are still in
* `neonctl/`, so `DEFAULT` is recorded as `../neonctl/credentials.json` rather than a
* relative name that would resolve to a file that isn't there.
*/
const upsertProfile = (dir, name, entry) => {
	assertValidProfileName(name);
	const path = profilesFilePath(dir);
	const read = inspectProfiles(dir);
	if (read.kind === "unusable") throw new Error(`${read.reason}. Refusing to rewrite it, because doing so would discard the profiles it defines. Fix or delete the file, then re-run.`);
	const file = read.kind === "ok" ? read.file : {
		version: 1,
		profiles: { [DEFAULT_PROFILE]: { credentials: storedPointer(path, credentialsPath(dir)) } }
	};
	file.profiles[name] = {
		credentials: storedPointer(path, entry.credentials),
		...entry.label ? { label: entry.label } : {},
		...entry.userId ? { userId: entry.userId } : {}
	};
	writeProfiles(path, file);
};
/** Remove an entry. Returns false when it wasn't there. */
const removeProfileEntry = (dir, name) => {
	const path = profilesFilePath(dir);
	const file = readProfiles(dir);
	if (!file?.profiles[name]) return false;
	delete file.profiles[name];
	writeProfiles(path, file);
	return true;
};
const onlyDefaultRemains = (file) => {
	const names = Object.keys(file.profiles);
	return names.length === 0 || names.length === 1 && names[0] === "DEFAULT";
};
const canDropProfilesFile = (file) => {
	if (!onlyDefaultRemains(file)) return false;
	const remaining = file.profiles[DEFAULT_PROFILE];
	return remaining === void 0 || !isKeyringPointer(remaining.credentials);
};
const locationForName = (dir, name) => locationOf(resolveProfile(dir, name));
const newProfileLocation = (dir, name, storage) => storage === "keyring" ? {
	profile: name,
	storage: CRED_STORAGE_KEYRING
} : {
	profile: name,
	storage: CRED_STORAGE_FILE,
	path: newProfileCredentialsPath(dir, name)
};
const profilesUsingPath = (dir, path, except) => {
	const resolved = resolve(path);
	return listProfiles(dir).filter((profile) => profile.name !== except && profile.storage === "file" && resolve(profile.credentialsPath) === resolved).map((profile) => profile.name);
};
const listProfiles = (dir) => {
	const read = inspectProfiles(dir);
	if (read.kind === "unusable") throw new Error(`${read.reason}. Fix or delete the file — every named profile is defined in it.`);
	const file = read.kind === "ok" ? read.file : null;
	if (!file) return [resolveProfile(dir, DEFAULT_PROFILE)];
	const names = Object.keys(file.profiles);
	if (!names.includes("DEFAULT")) names.unshift(DEFAULT_PROFILE);
	return names.map((name) => resolveProfile(dir, name));
};
const writeProfiles = (path, file) => {
	writeSecretFile(path, `${JSON.stringify(file, null, 2)}\n`);
};
const resolveEntryPath = (dir, entry) => isAbsolute(entry) ? entry : resolve(profilesDir(dir), entry);
/** `profiles.json` may sit in the legacy directory, so entries resolve against its own dir. */
const profilesDir = (dir) => resolve(profilesFilePath(dir), "..");
/** A relative file named `keyring` would otherwise collide with the storage sentinel. */
const storedPointer = (profilesPath, credentials) => {
	if (isKeyringPointer(credentials)) return KEYRING_CREDENTIALS;
	const base = resolve(profilesPath, "..");
	const abs = isAbsolute(credentials) ? credentials : resolve(base, credentials);
	const rel = relative(base, abs);
	const stored = rel && !isAbsolute(rel) ? rel : abs;
	return stored === "keyring" ? `./${KEYRING_CREDENTIALS}` : stored;
};
function nonEmpty(value) {
	if (typeof value !== "string") return void 0;
	const trimmed = value.trim();
	return trimmed === "" ? void 0 : trimmed;
}
//#endregion
export { DEFAULT_PROFILE, KEYRING_CREDENTIALS, PROFILES_FILE, assertProfilesUsable, assertValidProfileName, canDropProfilesFile, credentialsDisplay, inspectProfiles, isKeyringPointer, listProfiles, locationForName, locationOf, newProfileCredentialsPath, newProfileLocation, onlyDefaultRemains, profilesFilePath, profilesUsingPath, readProfiles, removeProfileEntry, resolveProfile, selectProfileName, upsertProfile };

//# sourceMappingURL=profiles.js.map